import { getGroupedActions } from '@/helpers/grouping'
import { toggleArrByKey, getUniqueArr } from '@/helpers/helpers'
import { MAX_NUM_OF_PLAYERS } from '@/helpers/constantsNew'

function initPlayersHands() {
  return Array.from({ length: MAX_NUM_OF_PLAYERS }, () => [])
}

function getInitialState() {
  const res = {
    suits: {
      suited: [],
      offsuited: [],
      exclude_suited: [],
      exclude_offsuited: [],
    },
    ev_comparison_actions: {
      action_primary: null,
      action_secondary: null,
      compare_by_best_ev_action: false,
    },

    categories: [],

    actions: [],
    actions_highlighted: [],
    hands_players: initPlayersHands(),
  }
  return res
}

const getters = {
  sol_filter_active: (state, getters) => !!getters.sol_filter_combos.active_rules,
  sol_filter_actionsHighlighted: state => state.actions_highlighted,
  sol_filter_playersHandsIds: state => state.hands_players,
  sol_filter_actions: state => state.actions,
  sol_filter_combos: (state, getters, _rootState, rootGetters) => {
    const { categories, actions } = state

    const suits = getters.sol_filter_suits

    const res = {
      status: 'inactive',
      filter_intersection: rootGetters.sw_study_filter_intersection === 'swv_filter_intersection',
      active_rules: 0,
      suits,
      categories,
      actions,
    }
    const actionsLength = actions.length
    const categoriesLength = categories.length

    // just some numbers to recompute getters active_rules with different value for suits
    const snumbers = {
      s: 0.99,
      h: 0.98,
      d: 0.97,
      c: 0.96,
    }
    const suitsLength =
      suits.suited.reduce((a, b) => snumbers[b] + a, 0) +
      suits.offsuited.reduce((a, b) => snumbers[b] + a, 0) +
      suits.exclude_suited.reduce((a, b) => snumbers[b] + a, 0) +
      suits.exclude_offsuited.reduce((a, b) => snumbers[b] + a, 0)

    res.active_rules = actionsLength + categoriesLength + suitsLength
    if (res.active_rules) {
      res.status =
        actionsLength === res.active_rules
          ? 'only_actions'
          : !actionsLength
            ? 'without_actions'
            : 'with_actions'
    }
    // console.log('sol_filter_combos', res)
    return res
  },
  sol_filter_categories: state => state.categories,
  sol_filter_suits: (state, getters) => {
    const { suits } = state
    const res = {
      suited: suits.suited || [],
      offsuited: suits.offsuited || [],
      exclude_suited: suits.exclude_suited || [],
      exclude_offsuited: suits.exclude_offsuited || [],
    }
    // console.log('res', sol_filter_suits)
    return res
  },
  sol_filter_ev_comparison: (state, getters) => {
    const grouping = getters.sw_actions_grouping

    let actions = getters.sol_data_solution_picked || []
    // actions = [actions[0] || null].filter(i => i) //  for single action spots
    if (grouping !== 'swv_grouping_none') {
      const res = getGroupedActions({
        actions,
        grouping,
        groupValues: [],
        raisesFirst: true,
      })
      actions = res
    }

    // reassign order
    // rtc and htc grouping have reversed order than this (so are using 'order')
    const l = actions.length - 1
    actions = actions.map((i, idx) => ({
      ...i,
      action_id: i.code || i.id,
      order: idx,
      order_reverse: l - idx,
    }))

    const hSpot = getters.sol_data_hSpot
    const { action_primary, action_secondary, compare_by_best_ev_action } =
      state.ev_comparison_actions

    const actionsPicked =
      action_primary &&
      action_secondary &&
      actions.some(aa => aa.action_id === action_primary?.action_id) &&
      actions.some(aa => aa.action_id === action_secondary?.action_id)

    const a_primary = actionsPicked ? action_primary : actions[actions.length - 1]
    const a_secondary = actionsPicked ? action_secondary : actions[actions.length - 2] || a_primary // for single action spots
    const res = {
      disabled: !a_primary || !a_secondary,
      pot: hSpot?.pot || 0,
      actions,
      action_primary: a_primary || {},
      action_secondary: a_secondary || {},
      compare_by_best_ev_action,
    }
    // console.log(res)
    return res
  },
}

const storeActions = {
  sol_filters_toggleCategory(
    { state, commit, dispatch, getters },
    { category, applyFilterActionName = 'sol_data_applyFilter' },
  ) {
    const res = toggleArrByKey({
      arr: state.categories,
      item: category,
      key: 'name',
    })
    dispatch('sol_filters_apply', { key: 'categories', value: res, applyFilterActionName })
  },
  sol_filters_toggleCategories(
    { state, commit, dispatch, getters },
    { categories, applyFilterActionName = 'sol_data_applyFilter' },
  ) {
    let res = []
    if (categories.every(c => state.categories.some(i => i.name === c.name))) {
      res = state.categories.filter(c => !categories.some(i => i.name === c.name))
    } else {
      res = [...state.categories, ...categories]
    }
    res = getUniqueArr({ arr: res, key: 'name' })
    dispatch('sol_filters_apply', { key: 'categories', value: res, applyFilterActionName })
  },
  sol_filters_handle_mode_change({ state, commit, dispatch, getters }) {
    if (getters.sol_filter_combos.status !== 'inactive') {
      dispatch('sol_data_applyFilter')
    }
  },
  sol_filters_apply(
    { state, commit, dispatch, getters, rootGetters },
    { key, value, applyFilterActionName = 'sol_data_applyFilter' },
  ) {
    commit('sol_filters_set', { key, value })

    // reset hanging pinned combos
    commit('sol_detail_set', {
      key: 'players_detail',
      playerProperty: 'group_id',
      value: '',
    })

    if (!applyFilterActionName) return
    dispatch(applyFilterActionName)
  },
  sol_filters_cleanup({ state, commit, dispatch, getters }, { actions, allCategories }) {
    const heroIndex = 0
    if (!getters.sol_filter_active) return

    state.categories = state.categories
      .map(c =>
        allCategories[c.categoryId].find(
          cc => cc.name === c.name && cc.players[heroIndex].total_occurrence,
        ),
      )
      .filter(i => i)

    state.actions = state.actions.filter(a => actions.some(aa => aa.code === a.code))
    dispatch('sol_data_applyFilter')
  },
  sol_filters_reset({ state, commit, dispatch, getters }) {
    const res = getInitialState()
    Object.keys(res).forEach(key => {
      state[key] = res[key]
    })
  },
}

const mutations = {
  sol_filters_set(state, { key, value }) {
    state[key] = value
  },
}

export default {
  state: getInitialState(),
  getters,
  actions: storeActions,
  mutations,
}
